<!-- PointCloudView.vue -->
<script lang="ts">
import {defineComponent, computed, onMounted, type ComputedRef, ref, watch, type Ref} from 'vue'
import { useRoute } from 'vue-router'
import MapComponent from '@/components/MapComponent.vue'
import { useMapStore } from '@/stores/mapStore'
import VectorSource from 'ol/source/Vector.js'
import FileListVue from '@/components/FileList.vue'
import WebGLPointsLayer from 'ol/layer/WebGLPoints'
import GeoJSON from 'ol/format/GeoJSON'
import WKT from 'ol/format/WKT'
import { Style, Circle as CircleStyle, Fill } from 'ol/style'
import Overlay from 'ol/Overlay'
import Chart from 'chart.js/auto'
import Collection from 'ol/Collection.js'
import Feature from 'ol/Feature.js'
import Point from 'ol/geom/Point.js'
import { fromLonLat, toLonLat } from 'ol/proj.js'
import { Polygon } from 'ol/geom.js'
import { Vector as VectorLayer } from 'ol/layer.js'
import { Stroke } from 'ol/style.js'

// vue date range
import { useDatasets } from '@/composables/useDatasets'
import type { Dataset } from '@/composables/useDatasets'
import { add } from 'ol/coordinate'

import { useSites } from '@/composables/useSites'

// info drawer
import { useDrawer } from '@/composables/useDrawer'

export default defineComponent({
  components: {
    MapComponent,
    FileListVue
  },

  setup() {
    const drawer = useDrawer()

    const route = useRoute()
    const siteId = computed(() => route.params.site_id) as ComputedRef<string>
    console.log('Site ID:', siteId.value)
    console.log('route params:', route.params)
    const mapStore = useMapStore()
    const datasetsAdapter = useDatasets(route.params.site_id as string)
    const sitesAdapter = useSites()
    const selectedDataset: Ref<Dataset | null> = ref(null)
    const slider = document.getElementById('coherence-threshold')
    const cohThreshold = ref(0.7)
    const baseWidth = ref(720)
    const pointSize = ref(2)
    const selectedPoint = ref(null)
    const siteObject = ref(null)
    const selectedColorRamp = ref('blackwhite')

    const categories = ref([
      { name: 'Structural Health Monitoring', key: 'S', route_key: 'shm', active: true },
      { name: 'Coastal Monitoring', key: 'C', route_key: 'coastal', active: false },
      { name: 'Environmental Monitoring', key: 'E', route_key: 'environmental', active: false },
      { name: 'Alerts and change detection', key: 'A', route_key: 'alerts', active: false }
    ]);
    const selectedCategory = ref('S')

    const updatePopupWidth = (mapWidth: number) => {
      // Set the width of the popup as a proportion of the map width
      baseWidth.value = mapWidth * 0.5
    }

    const featuresContainer = ref(new Collection())
    const globalGeoJsonFormat = new GeoJSON()

    console.log(siteId.value)
    const handleDatasetChange = (dataset: Dataset) => {
      // search for the dataset in the datasets array
      // const dataset = datasetsAdapter.datasets.value.find(
      //   (dataset) => dataset.name === datasetName
      // ) as Dataset
      selectedDataset.value = dataset
      console.log(`Selected dataset: ${selectedDataset.value?.id} - ${selectedDataset.value?.name}`)
      // zoomToDataSet();
    }



    const set_preset = () => {
      if (selectedCategory.value === 'S') {
        cohThreshold.value = 0.8
      } else if (selectedCategory.value === 'C') {
        cohThreshold.value = 0.6
      } else if (selectedCategory.value === 'E') {
        cohThreshold.value = 0.4
      } else if (selectedCategory.value === 'A') {
        cohThreshold.value = 0.7
      }
      // set the active category
      categories.value.forEach((cat) => {
        cat.active = cat.key === selectedCategory.value
      })
    }

    // check route params for a 'psi_settings' key
    if (route.params.psi_settings) {
      console.log('Route params:', route.params.psi_settings)
      const psi_settings = route.params.psi_settings
      console.log('Parsed psi_settings:', psi_settings)
      // psi_settings might be 'shm', 'coastal', 'environmental', 'alerts'
      if (psi_settings) {
        const cat = categories.value.find((cat) => cat.route_key === psi_settings)
        if (cat) {
          selectedCategory.value = cat.key
          console.log('Selected category:', selectedCategory.value)
          set_preset()
        }
      }
    }
    console.log('123')

    const pointsLayer: Ref<WebGLPointsLayer<VectorSource> | null> = ref(null)

    function hsvToRgb(h: number, s: number, v: number) {
      // Convert hue to a scale between 0 and 240 degrees (hue in degrees)
      h = h * 240 // Scale the input hue [0, 1] to [0, 240]

      let r = 0,
        g = 0,
        b = 0
      const i = Math.floor(h / 60) // Find the sector of the hue
      const f = h / 60 - i // Fractional part of the hue
      const p = v * (1 - s) // p = value * (1 - saturation)
      const q = v * (1 - f * s) // q = value * (1 - f * saturation)
      const t = v * (1 - (1 - f) * s) // t = value * (1 - (1 - f) * saturation)

      switch (i) {
        case 0:
          ;(r = v), (g = t), (b = p)
          break
        case 1:
          ;(r = q), (g = v), (b = p)
          break
        case 2:
          ;(r = p), (g = v), (b = t)
          break
        case 3:
          ;(r = p), (g = q), (b = v)
          break
        case 4:
          ;(r = t), (g = p), (b = v)
          break
        case 5:
          ;(r = v), (g = p), (b = q)
          break
      }

      // Convert RGB to [0, 255] range
      return [Math.round(b * 255), Math.round(g * 255), Math.round(r * 255)]
    }

    function linspace(start: number, end: number, numStops: number) {
      const step = (end - start) / (numStops - 1)
      const result = []

      for (let i = 0; i < numStops; i++) {
        result.push(start + i * step)
      }

      return result
    }
    function generateColorMap(threshold: number) {
      // Different color schemes for coherence visualization
      let lowColor, highColor;
      
      switch (selectedColorRamp.value) {
        case 'blackwhite':
          lowColor = 'rgb(0, 0, 0)';      // Black for coherence = 0
          highColor = 'rgb(255, 255, 255)'; // White for coherence = 1
          break;
        case 'redgreen':
          lowColor = 'rgb(255, 0, 0)';     // Red for coherence = 0
          highColor = 'rgb(0, 255, 0)';    // Green for coherence = 1
          break;
        case 'blueyellow':
          lowColor = 'rgb(0, 0, 255)';     // Blue for coherence = 0
          highColor = 'rgb(255, 255, 0)';  // Yellow for coherence = 1
          break;
        case 'purpleorange':
          lowColor = 'rgb(128, 0, 128)';   // Purple for coherence = 0
          highColor = 'rgb(255, 165, 0)';  // Orange for coherence = 1
          break;
        default:
          lowColor = 'rgb(0, 0, 0)';
          highColor = 'rgb(255, 255, 255)';
      }

      return [
        'interpolate',
        ['linear'],
        ['get', 'coherence'],
        0, lowColor,
        1, highColor
      ]
    }

    const layerSetup = () => {
      console.log('Setting up WebGLPointsLayer')
      // Set up the WebGLPointsLayer
      pointsLayer.value = new WebGLPointsLayer({
        source: new VectorSource(),
        style: {
          'circle-radius': pointSize.value,
          'circle-fill-color': generateColorMap(cohThreshold.value),
          'circle-rotate-with-view': false,
          'circle-displacement': [0, 0]
        }
      })
      if (!mapStore.map) {
        throw new Error('Map not initialized')
      }
      const thisMap = mapStore.map
      if (!pointsLayer.value) {
        throw new Error('Points layer not initialized')
      }
      thisMap.addLayer(pointsLayer.value)
    }

    const pointsSource = () => {
      if (!pointsLayer.value) {
        throw new Error('Points layer not initialized')
      }
      const source = pointsLayer.value.getSource()
      if (!source) {
        // we need to create a new source
      }
      return <VectorSource>pointsLayer.value.getSource()
    }

    function addPointsToMap(batchData: any) {
      console.log(`addPointsToMap: Processing ${batchData.length} points`)
      
      // Use Object.values(batchData) to get an array of the point data
      const features = Object.values(batchData).map((point: any) => {
        // The order of the elements is: lon, lat, coherence, velocity, height_error
        const lon = point[1]
        const lat = point[0]
        const coherence = point[2]
        const velocity = point[3]
        const height_error = point[4]

        // Create a feature for each point
        const feature = new Feature({
          geometry: new Point(fromLonLat([lon, lat])), // Convert coordinates to map projection
          coherence,
          velocity,
          height_error
        })
        return feature
      })

      console.log(`addPointsToMap: Created ${features.length} features`)

      // Add features to the source
      const source = pointsSource()
      const beforeCount = source.getFeatures().length
      source.addFeatures(features)
      const afterCount = source.getFeatures().length
      
      console.log(`addPointsToMap: Map source before: ${beforeCount}, after: ${afterCount}, added: ${afterCount - beforeCount}`)
    }
    const zoomToDataSet = () => {
      if (!mapStore.map) {
        console.log('No map')
        return
      }
      // zoom to the extent of the dataset
      if (!selectedDataset.value) {
        console.log('No dataset selected')
        return
      }
      if (!selectedDataset.value.extent) {
        console.log('No extent found for dataset')
        return
      }
      // type check that extent is a string
      if (typeof selectedDataset.value.extent !== 'string') {
        console.log('Extent is not a string')
        return
      }
      var extent: string = selectedDataset.value.extent
      if (!extent) {
        console.log('No extent found for dataset')
        return
      }
      if (!mapStore.map) {
        throw new Error('Map not initialized')
      }
      const wkt = new WKT()
      console.log('Original extent:', extent)
      // openlayers doesn't like the SRID=4326; prefix
      extent = extent.replace(/SRID=4326;\s*/i, '')
      console.log('Modified extent:', extent)
      const extentGeometry = wkt.readGeometry(extent)
      // const polygon = wkt.readGeometry("SRID=4326;POLYGON ((0 1, 0 2, 1 2, 1 1, 0 1))");
      console.log(extentGeometry)
      const transformedExtent = extentGeometry.transform(
        'EPSG:4326',
        mapStore.map.getView().getProjection()
      )


      // OL expects polygon to be:
      // [[WN], [WS], [ES], [EN], [WN]]
      console.log('aloha')
      const olSiteCoords = extentGeometry.getCoordinates()[0].map((coord) => fromLonLat(coord))
      const sitePolygon = new Polygon([olSiteCoords])
      const siteFeature = new Feature(sitePolygon)
      // Create a vector source and layer
      const vectorSource = new VectorSource({
        features: [siteFeature]
      })
      const vectorLayer = new VectorLayer({
        source: vectorSource,
        // style: polyStyle
      })
      // Add the vector layer to the map
      mapStore.map.addLayer(vectorLayer)
      // mapStore.map.getView().fit(transformedExtent.getExtent(), { duration: 2000 })
      mapStore.map.getView().fit(sitePolygon, { padding: [100, 100, 100, 100] })

    }

    const zoomToExtent = (extent: string) => {
      if (!mapStore.map) {
        throw new Error('Map not initialized')
      }
      const wkt = new WKT()

      // openlayers doesn't like the SRID=4326; prefix
      extent = extent.replace(/SRID=4326;\s*/i, '')
      console.log('Modified extent:', extent)
      const extentGeometry = wkt.readGeometry(extent) as Polygon
      const transformedExtent = extentGeometry.transform(
        'EPSG:4326',
        mapStore.map.getView().getProjection()
      )

      const polyStyle = new Style({
        fill: new Fill({
          color: 'rgba(255, 255, 255, 0.0)'
        }),
        stroke: new Stroke({
          color: '#dddddd',
          width: 3,
          lineDash: [3, 1],
          lineCap: 'butt'
        })
      })
      const siteFeature = new Feature(transformedExtent)
      // Create a vector source and layer
      const vectorSource = new VectorSource({
        features: [siteFeature]
      })
      const vectorLayer = new VectorLayer({
        source: vectorSource,
        style: polyStyle
      })
      // Add the vector layer to the map
      mapStore.map.addLayer(vectorLayer)
      mapStore.map.getView().fit(transformedExtent, { padding: [100,100,100,100] })
    }

    const isLoading = ref(false)
    const totalFeatures = ref(0)
    const loadedFeatures = ref(0)
    const loadingProgress = computed(() => {
      if (totalFeatures.value === 0) return 0
      // Calculate progress based on batches processed, not individual features
      // since filtering can significantly reduce the actual loaded count
      const totalBatches = Math.ceil(totalFeatures.value / 10000)
      const processedFeatures = Math.min(loadedFeatures.value, totalFeatures.value)
      const result = Math.floor((processedFeatures / totalFeatures.value) * 100)
      console.log(`Progress: ${processedFeatures}/${totalFeatures.value} = ${result}%`)
      return result
    })

    const findFeaturesForDataset = () => {
      if (!selectedDataset.value) {
        console.log('No dataset selected')
        return
      }
      if (!pointsLayer.value) {
        console.log('No points layer')
        layerSetup()
      } else {
        // Clear the existing features
        pointsSource().clear()
        layerSetup()
      }

      // Reset loading state
      isLoading.value = true
      loadedFeatures.value = 0
      totalFeatures.value = 0

      // Query the API for the feature count of the selected dataset
      fetch(`/api/scatterer-info/?limit=1&dataset=${selectedDataset.value.id}`)
        .then((response) => {
          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`)
          }
          return response.json()
        })
        .then((data) => {
          // Get the total feature count
          totalFeatures.value = data.count
          console.log(`Total features for dataset: ${totalFeatures.value}`)
          if (totalFeatures.value === 0) {
            console.log('No features found for dataset')
            isLoading.value = false
            return
          }

          // Function to load features in batches of 10,000
          console.log(`Loading ${totalFeatures.value} features`)
          const loadFeaturesInChunks = async () => {
            // Load batches sequentially instead of all at once to prevent overwhelming the browser/server
            for (let offset = 0; offset < totalFeatures.value; offset += 10000) {
              const batchSize = Math.min(10000, totalFeatures.value - offset)
              
              try {
                console.log(`Loading batch: ${offset} to ${offset + batchSize}`)
                const response = await fetch(
                  `/api/scatterer-info/?limit=${batchSize}&offset=${offset}&dataset=${selectedDataset.value!.id}`
                )
                
                if (!response.ok) {
                  throw new Error(`HTTP error! status: ${response.status}`)
                }
                
                const batchData = await response.json()
                const data = batchData.data
                const bdVals = Object.values(data)
                
                console.log(`Batch ${offset}: Received ${bdVals.length} raw points from API`)
                
                // Filter out null values
                const filtered = bdVals.filter(
                  (point: any) =>
                    point[0] !== null &&
                    point[1] !== null &&
                    point[2] !== null &&
                    point[3] !== null &&
                    point[4] !== null
                )
                
                console.log(`Batch ${offset}: ${filtered.length} points after null filtering (removed ${bdVals.length - filtered.length})`)
                
                // Filter by coherence threshold
                const filteredByCoh = filtered.filter((point: any) => point[2] > cohThreshold.value)
                
                console.log(`Batch ${offset}: ${filteredByCoh.length} points after coherence filtering (removed ${filtered.length - filteredByCoh.length}, threshold: ${cohThreshold.value})`)
                
                // Add points to map
                addPointsToMap(filteredByCoh)
                
                // Update progress with actual loaded features
                loadedFeatures.value += filteredByCoh.length
                console.log(`Batch ${offset}: Added ${filteredByCoh.length} points to map. Total loaded: ${loadedFeatures.value}`)
                console.log(`Running totals - API: ${(offset + batchSize)}, Loaded: ${loadedFeatures.value}`)
                
                // Small delay to prevent overwhelming the browser
                await new Promise(resolve => setTimeout(resolve, 50))
                
              } catch (error) {
                console.error(`Error loading batch at offset ${offset}:`, error)
                // Continue with next batch instead of failing completely
              }
            }
            
            isLoading.value = false
            console.log(`Finished loading. Total features loaded: ${loadedFeatures.value}`)
          }

          // Start loading features in chunks
          loadFeaturesInChunks()
            .then(() => console.log('All features loaded'))
            .catch((error) => {
              console.error('Error loading features:', error)
              isLoading.value = false
            })
        })
        .catch((error) => {
          console.error('Error fetching feature count:', error)
          isLoading.value = false
        })
    }

    onMounted(() => {
      const map = mapStore.map
      if (!map) {
        throw new Error('Map not initialized')
      }

      datasetsAdapter.fetchDatasets().then((response) => {
        console.log(datasetsAdapter.datasets.value)
        // zoom to first dataset extent
        if (datasetsAdapter.datasets.value.length > 0) {
          zoomToExtent(datasetsAdapter.datasets.value[0].extent)
        } else {
          // zoom to the site extent
          sitesAdapter.fetchItems().then(() => {

            const site = sitesAdapter.sites.value.find((site) => site.name === siteId.value)
            console.log('Site:', site)
            // convert the [N, E, S, W] string to a WKT polygon
            const noBrackets = site.geometry.slice(1, site.geometry.length - 1)
            const limits = noBrackets.split(',').map((coord) => parseFloat(coord))
            const wktpolygon = `POLYGON((${limits[1]} ${limits[0]}, ${limits[3]} ${limits[0]}, ${limits[3]} ${limits[2]}, ${limits[1]} ${limits[2]}, ${limits[1]} ${limits[0]}))`
            if (site) {
              zoomToExtent(wktpolygon)
            }
          })
        }
      })

      const pop_container = document.getElementById('popup')
      const pop_content = document.getElementById('popup-content')
      const pop_closer = document.getElementById('popup-closer')

      // Make sure these are not null
      if (pop_container === null || pop_content === null || pop_closer === null) {
        throw new Error('popup elements are null')
      }

      const pop_overlay = new Overlay({
        element: pop_container,
        autoPan: {
          animation: {
            duration: 500
          }
        },
        positioning: 'bottom-left'
      })
      pop_closer.onclick = function () {
        pop_overlay.setPosition(undefined)
        pop_closer.blur()
        return false
      }

      // Add an onclick-event to the map
      map.on('click', (event) => {
        if (!selectedDataset.value) {
          console.log('No dataset selected')
          return
        }

        // Get the coordinates of the pointer event
        const coords = event.coordinate
        // Y axis label
        console.log('Setting popup position to: ', coords)

        pop_overlay.setPosition(coords)
        // translate the pop_overlay by -100% of height
        const heightOfPopup = pop_container.clientHeight
        // Set the width of the popup as a proportion of the map width
        updatePopupWidth(map.getSize()[0])

        // shift slightly to the left to account for the popup arrow position,
        // and shift up by the height of the arrow
        pop_overlay.setOffset([-48, -heightOfPopup - 10])

        const coordsFormat1 = coords.map((coord) => coord.toFixed(0)).join(', ')

        // coordinates style - seperate little grey box
        const coordsStyle =
          'background-color: rgba(200, 200, 200, 0.5); padding: 5px; border-radius: 5px;'
        // const addStyle => (message: string) => {
        //   return `<span style="${coordsStyle}">${message}</span>`
        // }
        // Add the coordinates to the popup content
        // pop_content.innerHTML = `<p>Coordinates: ${coordsFormat2}</p>`
        // With style:

        pop_content.innerHTML = `<h3>Coherence - ${selectedDataset.value.name}</h3>`

        pop_content.innerHTML += `<p>Map Coordinates (EPSG:3857): <span style="${coordsStyle}">[${coordsFormat1}] </span></p>`
        // Add the coordinates in lat/lon
        const coordsFormat2 = toLonLat(coords)
          .map((coord) => coord.toFixed(4))
          .join(', ')
        // Flip xy so it's lat, lon
        const lat = coordsFormat2.split(',')[1]
        const lon = coordsFormat2.split(',')[0]
        pop_content.innerHTML += `<p>Geographic Coordinates (EPSG:4326): <span style="${coordsStyle}">[${lat}, ${lon}]</span></p>`
        // Add displacement rate
        pop_content.innerHTML += `<p>Model displacement rate: <span style="${coordsStyle}"> LOADING_VELOCITY mm/year</span></p>`
        pop_content.innerHTML += `<p>Coherence: <span style="${coordsStyle}">LOADING_COHERENCE</span></p>`
        pop_content.innerHTML += '<canvas id="time-series-chart"></canvas>'
        // Add some dummy dates and values for the chart
        var dates = []
        var values = []

        // get dates from the api
        fetch(`/api/psi-datasets/${selectedDataset.value.id}/`)
          .then((response) => {
            if (!response.ok) {
              throw new Error(`HTTP error! status: ${response.status}`)
            }
            return response.json()
          })
          .then((data) => {
            console.log(data.date_series)

            dates = data.date_series
            // Replace 'LOADING' with the actual values
          })

        // get the nearest scatterer to the clicked point
        fetch(`/api/nearest-scatterer/?dataset=${selectedDataset.value.id}&lat=${lat}&lon=${lon}`)
          .then((response) => {
            if (!response.ok) {
              throw new Error(`HTTP error! status: ${response.status}`)
            }
            console.log(response)
            return response.json()
          })
          .then((nearestScatterer) => {
            console.log(nearestScatterer)
            // Use the id to get the time series data
            const scattererId = nearestScatterer.time_series
            fetch(
              `/api/psi-scatterer-timeseries/?dataset=${selectedDataset.value.id}&id=${scattererId}`
            )
              .then((response) => {
                if (!response.ok) {
                  throw new Error(`HTTP error! status: ${response.status}`)
                }
                return response.json()
              })
              .then((dataJson) => {
                console.log(dataJson.values)
                console.log('SELECTED POINT')
                console.log(nearestScatterer)
                selectedPoint.value = nearestScatterer
                var vel_3_sig_fig = (nearestScatterer.velocity * 1000).toFixed(2)
                pop_content.innerHTML = pop_content.innerHTML.replace(
                  'LOADING_VELOCITY',
                  vel_3_sig_fig
                )
                var coh_3_sig_fig = nearestScatterer.coherence.toFixed(3)
                pop_content.innerHTML = pop_content.innerHTML.replace(
                  'LOADING_COHERENCE',
                  coh_3_sig_fig
                )

                selectedPoint.value = nearestScatterer
                // Create a time series chart
                const formattedData = dates.reduce((acc, date, index) => {
                  acc[date] = dataJson.values[index]
                  return acc
                }, {})

                // Convert this into a chart.js time series chart
                // make some data with 'labels', 'datasets'
                const labels = Object.keys(formattedData)
                const data = {
                  labels: labels,
                  datasets: [
                    {
                      label: 'Displacement Time Series',
                      data: Object.values(formattedData),
                      fill: false,
                      borderColor: 'rgb(75, 192, 192)',
                      tension: 0.1
                    }
                  ]
                }
                // get the canvas
                const chartbox = document.getElementById('time-series-chart') as HTMLCanvasElement
                if (chartbox === null) {
                  throw new Error('chartbox is null')
                }

                // y axis label
                const yaxis = document.createElement('div')
                yaxis.innerHTML = 'Displacement (m)'
                yaxis.style.position = 'absolute'
                yaxis.style.top = '10px'
                yaxis.style.left = '10px'

                // To control the size of the chart we need to set the width of the canvas / parent div
                console.log(chartbox)
                console.log(chartbox.style.width)
                console.log(baseWidth.value)
                // set the canvas width
                console.log('Setting chart width to: ', baseWidth.value)
                chartbox.width = baseWidth.value

                // Get the canvas
                new Chart(chartbox, {
                  type: 'line',
                  data: data,
                  options: {
                    responsive: true,
                    scales: {
                      y: {
                        title: {
                          // Add this section
                          display: true, // Make sure the title is displayed
                          text: 'Displacement (m)', // Set the title text
                          color: 'black', // Optional: Set the color of the title
                          font: {
                            // Optional: Customize font properties
                            size: 16, // Font size
                            family: 'Arial' // Font family
                          }
                        }
                      }
                    }
                  }
                })
              })
              .catch((error) => console.error('Error fetching time series data:', error))
          })
          .catch((error) => console.error('Error fetching nearest scatterer:', error))
      })
      // Add the overlay to the map
      map.addOverlay(pop_overlay)

      // Add a vue watch for cohThreshold
      watch(cohThreshold, (newVal) => {
        console.log('Coh threshold changed:', newVal)
      }) // watch

      // Add a vue watch for selectedColorRamp
      watch(selectedColorRamp, (newVal) => {
        console.log('Color ramp changed:', newVal)
        // Update the layer style when color ramp changes
        if (pointsLayer.value) {
          pointsLayer.value.setStyle({
            'circle-radius': pointSize.value,
            'circle-fill-color': generateColorMap(cohThreshold.value),
            'circle-rotate-with-view': false,
            'circle-displacement': [0, 0]
          })
        }
      }) // watch

      // Add a vue watch for pointSize
      watch(pointSize, (newVal) => {
        console.log('Point size changed:', newVal)
        // if (pointsLayer.value) {
        //   pointsLayer.value.setStyle({
        //     'circle-radius': newVal,
        //     'circle-fill-color': [
        //       'interpolate',
        //       ['linear'],
        //       ['get', 'velocity'],
        //       -0.005, '#0000ff',
        //       -0.002, '#00aaaa',
        //       0, '#00ff00',
        //       0.002, '#aaaa00',
        //       0.005, '#ff0000'
        //     ],
        //     'circle-rotate-with-view': false,
        // 'circle-displacement': [0, 0],
        // }
      }) // watch
    }) // onMounted

    return {
      siteId,
      datasetsAdapter,
      cohThreshold,
      pointSize,
      handleDatasetChange,
      findFeaturesForDataset,
      selectedPoint,
      drawer,
      selectedCategory,
      categories,
      set_preset,
      isLoading,
      loadingProgress,
      totalFeatures,
      selectedColorRamp
    }
  }
})
</script>

<template>
  <div class="flex">
    <div class="w-1over3">
      <h1>{{ siteId }} Coherence Views</h1>
      <!-- Table for available point layers -->
      <table>
        <tr>
          <th>Coherence Layers</th>
        </tr>
        <tr v-for="(dataset, index) in datasetsAdapter.datasets.value" :key="index">
          <td v-if="dataset.id !== null">
            <input
              type="radio"
              :id="dataset.id"
              name="pointLayer"
              :value="dataset.name"
              @change="handleDatasetChange(dataset)"
            />
            <label :for="dataset.name">{{ dataset.displayName || dataset.name }}</label>
          </td>
          <td v-else>
            Error with dataset id
          </td>
        </tr>
      </table>
      <!-- Load button -->
      <Button @click="findFeaturesForDataset" :disabled="isLoading">Load</Button>

      <!-- Loading progress bar -->
      <div v-if="isLoading" class="mt-4">
        <ProgressBar 
          :value="loadingProgress" 
          :showValue="true"
          class="custom-progress"
          style="width: 100%"
        >
          <template #content="{ value }">
            Loading points: {{ Math.round(value) }}%
          </template>
        </ProgressBar>
      </div>

      <Fieldset legend="View Controls" :toggleable="true">
        <h2>Use Case Presets:</h2>
        <div class="card flex justify-center">
          <div class="flex flex-col gap-4">
            <div v-for="category in categories" :key="category.key" class="flex items-center gap-2">
              <RadioButton
                v-model="selectedCategory"
                :id="category.key"
                :value="category.key"
                @change="() => {
                  set_preset()
                }"
              />
              <!-- <input type="radio" :id="category.key" name="category" :value="category.key" /> -->
              <label :for="category.key">{{ category.name }}</label>
            </div>
          </div>
        </div>
        <!-- Table for view controls -->
        <h2>Controls</h2>
        <table>
          <!-- <tr>
            <th>Control</th>
            <th>Value</th>
          </tr> -->
          <!-- Dropdown for colour ramp preset -->
          <tr>
            <td>Colour Ramp</td>
            <td>
              <select id="colour-ramp" name="colour-ramp" v-model="selectedColorRamp">
                <option value="blackwhite">Black to White</option>
                <option value="redgreen">Red to Green</option>
                <option value="blueyellow">Blue to Yellow</option>
                <option value="purpleorange">Purple to Orange</option>
              </select>
            </td>
          </tr>
          <tr>
            <td>Point Size</td>
            <td>
              <input
                type="range"
                id="point-size"
                name="point-size"
                min="1"
                max="10"
                step="0.1"
                value="3"
                v-model.number="pointSize"
              />
              {{ pointSize }}
              <Button @click="drawer.openDrawer('Point Size', ['This is the size of the points rendered on the map'])">?</Button>
            </td>
          </tr>
          <tr>
            <td>Coherence Threshold</td>

            <td>
              <input
                type="range"
                id="coherence-threshold"
                name="coherence-threshold"
                min="0"
                max="1"
                step="0.01"
                value="0.7"
                v-model="cohThreshold"
              />
              {{ cohThreshold }}
              <Button
                @click="
                  drawer.openDrawer('Coherence Threshold', [
                    `Coherence in InSAR (Interferometric Synthetic Aperture Radar) data processing is a measure of how similar the radar signals are between two different observations of the same area. It
        ranges from 0 (completely different signals) to 1 (perfectly identical signals).`,
                    `
        For deformation monitoring, coherence tells us how reliable the phase difference
        (interferogram) is for measuring ground displacement. High coherence means that the surface
        properties haven t changed much between radar acquisitions, so we can confidently extract
        deformation information. Low coherence, on the other hand, indicates changes due to
        vegetation growth, land use changes, or atmospheric noise, making deformation measurements
        less reliable.`
                  ])
                "
                >?</Button
              >
            </td>
          </tr>
          <tr>
            <td>Date Range</td>
            <td>
              <input type="date" id="date-range" name="date-range" />
              to
              <input type="date" id="date-range" name="date-range" />
              <Button @click="drawer.openDrawer('Date Range', ['The range of data to display'])">?</Button>
            </td>
          </tr>
        </table>
      </Fieldset>
    </div>
    <div class="w-2over3">
      <MapComponent />
    </div>
  </div>
  <!-- container -->
  <div id="popup" class="ol-popup">
    <a href="#" id="popup-closer" class="ol-popup-closer"></a>
    <div id="popup-content"></div>
  </div>
</template>

<style scoped>
.flex {
  display: flex;
}

.w-1over3 {
  width: 33.33%;
}

.w-2over3 {
  width: 66.67%;
}

#false-color-composite-list {
  display: none;
}

#page-header {
  text-align: center;
  color: var(--color-text);
  font-size: 24px;
}

/* Add a global class that forces the grid layour for container */
.site-hub-container {
  display: grid;
  grid-template-columns: 1fr 3fr;
  /* Two columns, adjust the width as needed */
}

/* Controls on the left */
.controls {
  /* background-color: #f0f0f0;  */
  /* Background color for the left column */
  padding: 20px;
  /* Add padding for spacing */
}

.control-box {
  border: var(--color-text) 1px double;
  padding: 10px;
}

/* Map container on the right */
.map-container {
  /* You can adjust the size of the map container here */
  width: 100%;
  height: 400px;
  /* Adjust the height as needed */
  position: relative;
  /* Ensure relative positioning for children elements */
}

/* Style map buttons as needed */
.map-container button {
  margin-top: 10px;
  /* Add margin between buttons and the map */
}

/* Style the links in the control panel as buttons */
.vertical-controls a {
  display: flex;
  flex-direction: column;
  padding: 10px;
  margin: 5px;
  border: 1px solid var(--color-border);
  border-radius: 5px;
  text-decoration: none;
  color: var(--color-text);
  background-color: var(--color-background);
}

.vertical-controls a {
  flex: 1;
  text-align: center;
}

/* Hover background */
.vertical-controls a:hover {
  background-color: var(--color-background-mute);
}

.horizontal-controls {
  display: flex;
  flex-direction: row;
  padding: 10px;
  justify-content: space-evenly;
}

.no-border {
  border: none;
}

.site-data-item {
  background-color: var(--color-background-mute);
  padding: 5px;
  margin: 5px;
  border: 1px solid var(--brand-color);
  border-radius: 5px;
  text-decoration: none;
  color: #000;
}

.site-data-item:hover {
  background-color: var(--color-background);
}

.ol-popup {
  position: absolute;
  background-color: white;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.2);
  padding: 15px;
  border-radius: 10px;
  border: 1px solid var(--color-border);
  color: black;
}

.ol-popup:after,
.ol-popup:before {
  top: 100%;
  border: solid transparent;
  content: ' ';
  height: 0;
  width: 0;
  position: absolute;
  pointer-events: none;
}

.ol-popup:after {
  border-top-color: white;
  border-width: 10px;
  left: 48px;
  margin-left: -10px;
}

.ol-popup:before {
  border-top-color: #cccccc;
  border-width: 11px;
  left: 48px;
  margin-left: -11px;
}

.ol-popup-closer {
  text-decoration: none;
  position: absolute;
  top: 2px;
  right: 8px;
}

.ol-popup-closer:after {
  content: '✖';
}

.custom-progress :deep(.p-progressbar) {
  height: 1.5rem;
  background: #f0f0f0;
}

.custom-progress :deep(.p-progressbar-value) {
  background: #4CAF50;
  transition: width 0.2s ease;
}

.custom-progress :deep(.p-progressbar-label) {
  color: #000;
  font-weight: bold;
}
</style>
